<!-- eslint-disable max-len -->
<template>
  <div
    v-if="data?.items && data.items.length > 0"
    class="gallery-case-studies__gallery"
    role="tablist"
  >
    <div
      v-for="(item, index) in data.items"
      :key="index"
      :class="['gallery-case-studies__card', { 'gallery-case-studies__card--active': activeTab === index }]"
      role="tab"
      :aria-selected="activeTab === index"
      :tabindex="activeTab === index ? 0 : -1"
      @click="activeTab !== index && setActiveTab(index)"
      @keydown.enter="activeTab !== index && setActiveTab(index)"
    >
      <img
        v-if="item.images?.[0]"
        :src="item.images[0]"
        :alt="item.title || 'Project image'"
        class="gallery-case-studies__card-img"
      >
      <div
        v-if="activeTab === index"
        class="gallery-case-studies__overlay"
      >
        <div class="gallery-case-studies__overlay-text">
          <p
            v-if="item.kicker"
            class="gallery-case-studies__kicker"
          >
            {{ item.kicker }}
          </p>
          <h3
            v-if="item.title"
            class="gallery-case-studies__card-title"
          >
            {{ item.title }}
          </h3>
          <p
            v-if="item.small_description"
            class="gallery-case-studies__card-desc"
          >
            {{ item.small_description }}
          </p>
        </div>
        <button
          class="btn-custom outline white gallery-case-studies__view-btn"
          @click.stop="openModal(item, 0)"
        >
          View Gallery
        </button>
      </div>
    </div>
  </div>

  <!-- Modal Lightbox -->
  <div
    v-if="showModal"
    class="gallery-case-studies__modal"
    @click="closeModal"
  >
    <div
      class="gallery-case-studies__modal__inner"
      @click.stop
    >
      <button
        class="gallery-case-studies__modal__inner__close"
        aria-label="Close"
        @click="closeModal"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4 4l8 8M12 4l-8 8"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>

      <div class="gallery-case-studies__modal__inner__content">
        <!-- Left Side - Image Gallery -->
        <div class="modal__wrapper">
          <!-- Main Slider -->
          <div
            ref="modalMainSlider"
            class="splide modal__wrapper__gallery"
            aria-label="Gallery Images"
          >
            <div class="splide__track">
              <ul class="splide__list">
                <li
                  v-for="(image, imgIdx) in modalData.images"
                  :key="imgIdx"
                  class="splide__slide"
                >
                  <img
                    :src="image"
                    :alt="`${modalData.title} - Image ${imgIdx + 1}`"
                  >
                </li>
              </ul>
            </div>
          </div>

          <!-- Thumbnail Slider -->
          <div
            v-if="modalData.images && modalData.images.length > 1"
            ref="modalThumbnailSlider"
            class="splide modal__wrapper__thumbnails"
          >
            <div class="splide__track">
              <ul class="splide__list">
                <li
                  v-for="(image, imgIdx) in modalData.images"
                  :key="imgIdx"
                  class="splide__slide"
                >
                  <button
                    :class="['modal__wrapper__thumbnails__list--item', { 'is-active': modalImageIndex === imgIdx }]"
                    @click="goToSlide(imgIdx)"
                  >
                    <img
                      :src="image"
                      :alt="`Thumbnail ${imgIdx + 1}`"
                    >
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Right Side - Text Content -->
        <div class="modal__container">
          <h3
            v-if="modalData.kicker"
            class="modal__container--kicker"
          >
            {{ modalData.kicker }}
          </h3>
          <h2
            v-if="modalData.title"
            class="modal__container--title"
          >
            {{ modalData.title }}
          </h2>
          <p
            v-if="modalData.small_description"
            class="modal__container--small_description"
          >
            {{ modalData.small_description }}
          </p>
          <p
            v-if="modalData.description"
            class="modal__container--description"
          >
            {{ modalData.description }}
          </p>
          <a
            v-if="modalData.button_text && modalData.button_link"
            :href="modalData.button_link"
            class="btn-custom filled yellow"
          >
            {{ modalData.button_text }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Splide from '@splidejs/splide';

export default {
  name: 'GalleryCaseStudies',
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      activeTab: 0,
      showModal: false,
      modalData: {},
      modalImageIndex: 0,
      modalMainSplide: null,
      modalThumbnailSplide: null,
      splideConfigs: {
        modal: {
          type: 'fade',
          rewind: true,
          pagination: true,
          arrows: false,
          height: '530px',
          breakpoints: {
            1280: { height: '400px' },
            800: { height: '400px' },
            600: { height: '350px' },
            400: { height: '300px' },
          },
        },
        thumbnails: {
          type: 'slide',
          rewind: true,
          perPage: 1,
          fixedWidth: '150px',
          fixedHeight: '100px',
          gap: '15px',
          pagination: false,
          arrows: false,
          breakpoints: {
            900: { fixedWidth: '100px', fixedHeight: '67px' },
          },
        },
      },
    };
  },
  beforeUnmount() {
    this.cleanupModalSplide();
    window.removeEventListener('keydown', this.handleKeydown);
  },
  created() {
    window.addEventListener('keydown', this.handleKeydown);
  },
  methods: {
    setActiveTab(index) {
      this.activeTab = index;
    },

    openModal(item, index) {
      if (this.showModal) return;

      this.modalData = item;
      this.modalImageIndex = index;
      this.showModal = true;
      document.body.style.overflow = 'hidden';

      this.$nextTick(() => {
        this.initializeModalSplide();
      });
    },

    closeModal() {
      this.cleanupModalSplide();
      this.showModal = false;
      document.body.style.overflow = '';
    },

    goToSlide(index) {
      this.modalImageIndex = index;
      if (this.modalMainSplide) {
        this.modalMainSplide.go(index);
      }
    },

    initializeModalSplide() {
      if (!this.$refs.modalMainSlider || !this.modalData.images?.length) return;

      this.cleanupModalSplide();

      this.modalMainSplide = new Splide(this.$refs.modalMainSlider, {
        ...this.splideConfigs.modal,
        start: this.modalImageIndex,
      });
      this.modalMainSplide.mount();

      if (this.$refs.modalThumbnailSlider && this.modalData.images.length > 1) {
        this.modalThumbnailSplide = new Splide(
          this.$refs.modalThumbnailSlider,
          this.splideConfigs.thumbnails,
        );
        this.modalThumbnailSplide.mount();
        this.modalMainSplide.sync(this.modalThumbnailSplide);

        this.$nextTick(() => {
          if (this.modalThumbnailSplide) {
            this.modalThumbnailSplide.go(this.modalImageIndex);
          }
        });
      }

      this.modalMainSplide.on('mounted move', () => {
        this.modalImageIndex = this.modalMainSplide.index;
        if (this.modalThumbnailSplide) {
          this.modalThumbnailSplide.go(this.modalImageIndex);
        }
      });
    },

    cleanupModalSplide() {
      if (this.modalMainSplide) {
        this.modalMainSplide.destroy();
        this.modalMainSplide = null;
      }
      if (this.modalThumbnailSplide) {
        this.modalThumbnailSplide.destroy();
        this.modalThumbnailSplide = null;
      }
    },

    handleKeydown(event) {
      if (!this.showModal || !this.modalMainSplide) return;

      const actions = {
        Escape: () => this.closeModal(),
        ArrowLeft: () => this.modalMainSplide.go('<'),
        ArrowRight: () => this.modalMainSplide.go('>'),
      };

      const action = actions[event.key];
      if (action) action();
    },
  },
};
</script>
